import { useMemo } from 'react';
import { SkuRedistributionPaneType, SkuRedistributionSubset } from '../../../redistributionTypes';
import { useSelectedPickingPositionFromUrl } from '../useSelectedPickingPositionFromUrl';
import { useSelectedPickingPositionExecutionFromUrl } from '../useSelectedPickingPositionExecutionFromUrl';
import { usePickingPositionParamsFromUrl } from '../usePickingPositionParamsFromUrl';
import { useStableComputedValue } from '../../../../../../hooks/useStableComputedValue';

interface CalculationRedistributionSectionData {
  redistributionData: SkuRedistributionSubset | undefined;
  showExecutionResult: boolean;
  executionResultLoading: boolean;
}

/**
 * Builds Source/Target pane data for the currently selected redistribution row.
 * Keeps the last coherent pane data visible while the URL selection and execution
 * result are briefly out of sync during keyboard row navigation.
 */
export const useCalculationRedistributionSectionData = (
  sourceOrTarget: SkuRedistributionPaneType,
): CalculationRedistributionSectionData => {
  const pickingPositionParams = usePickingPositionParamsFromUrl();
  const skuRedistribution = useSelectedPickingPositionFromUrl();
  const {
    execution: skuExecution,
    showExecutionResult,
    isExecutionResultPending,
  } = useSelectedPickingPositionExecutionFromUrl();

  const redistributionMatchesCurrentPosition =
    !!skuRedistribution &&
    !!pickingPositionParams &&
    skuRedistribution.sourceSku.skuId === pickingPositionParams.sourceSkuId &&
    skuRedistribution.targetSku.skuId === pickingPositionParams.targetSkuId;

  const currentRedistributionData = useMemo((): SkuRedistributionSubset | undefined => {
    if (!skuRedistribution) {
      return undefined;
    }

    if (sourceOrTarget === 'Source') {
      return {
        skuId: skuRedistribution.sourceSku.skuId,
        brandName: skuRedistribution.product.brand?.name ?? '',
        brandId: skuRedistribution.product.brand?.id ?? 0,
        brandQuintile: skuRedistribution.sourceSku.brandQuintile,
        quantity: skuRedistribution.quantity,
        totalMovedQuantity: skuRedistribution.totalQuantityFromSourceSku,
        store: skuRedistribution.sourceStore,
        quantityShipped: skuExecution?.shippedQuantity,
      };
    }

    return {
      skuId: skuRedistribution.targetSku.skuId,
      brandName: skuRedistribution.product.brand?.name ?? '',
      brandId: skuRedistribution.product.brand?.id ?? 0,
      brandQuintile: skuRedistribution.targetSku.brandQuintile,
      quantity: skuRedistribution.quantity,
      totalMovedQuantity: skuRedistribution.totalQuantityToTargetSku,
      store: skuRedistribution.targetStore,
      quantityDelivered: skuExecution?.deliveredQuantity,
    };
  }, [
    sourceOrTarget,
    skuExecution?.deliveredQuantity,
    skuExecution?.shippedQuantity,
    skuRedistribution,
  ]);

  const canUpdateDisplayedRedistributionData =
    !!currentRedistributionData &&
    redistributionMatchesCurrentPosition &&
    (!showExecutionResult || !isExecutionResultPending);

  const stableRedistributionData = useStableComputedValue(
    () => currentRedistributionData,
    !canUpdateDisplayedRedistributionData,
    [currentRedistributionData],
  );
  const redistributionData = stableRedistributionData ?? currentRedistributionData;
  const executionResultLoading =
    showExecutionResult &&
    isExecutionResultPending &&
    !!redistributionData &&
    redistributionData === currentRedistributionData;

  return {
    redistributionData,
    showExecutionResult,
    executionResultLoading,
  };
};
